import "./App.css";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Autocomplete,
  TextField,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  CssBaseline,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import PropTypes from "prop-types";

const API_BASE = "https://logicfy.ai/api";
// const API_BASE = "http://localhost:8080";

// --- Themes ---
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: { default: "#0f172a", paper: "#1e293b" },
    primary: { main: "#3b82f6" },
    success: { main: "#16a34a" },
    error: { main: "#dc2626" },
    divider: "#334155",
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 13,
  },
  components: {
    MuiTableCell: {
      styleOverrides: { root: { borderColor: "#334155" } },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: "none" } },
    },
  },
});

const lightTheme = createTheme({
  palette: {
    mode: "light",
    background: { default: "#f8fafc", paper: "#ffffff" },
    primary: { main: "#3b82f6" },
    divider: "#e2e8f0",
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 13,
  },
});

// --- Helpers ---
function descendingComparator(a, b, orderBy) {
  const av = orderBy === "RaceNumber" ? Number(a[orderBy]) : a[orderBy];
  const bv = orderBy === "RaceNumber" ? Number(b[orderBy]) : b[orderBy];
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  const stabilized = array.map((el, i) => [el, i]);
  stabilized.sort((a, b) => {
    const ord = comparator(a[0], b[0]);
    return ord !== 0 ? ord : a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

// --- Table Head ---
const headCells = [
  { id: "Selection", label: "Horse" },
  { id: "win", label: "Win" },
  { id: "place", label: "Place" },
  { id: "winShare", label: "Share%" },
  { id: "WinLimit", label: "Limit" },
  { id: "Percentage", label: "%" },
];

function EnhancedTableHead({ order, orderBy, onRequestSort }) {
  return (
    <TableHead>
      <TableRow>
        {headCells.map((hc) => (
          <TableCell
            key={hc.id}
            align="left"
            sortDirection={orderBy === hc.id ? order : false}
            sx={{
              padding: "4px 4px",
              fontWeight: 700,
              fontSize: "0.7rem",
              whiteSpace: "nowrap",
            }}
          >
            <TableSortLabel
              active={orderBy === hc.id}
              direction={orderBy === hc.id ? order : "asc"}
              onClick={() => onRequestSort(hc.id)}
            >
              {hc.label}
              {orderBy === hc.id ? (
                <Box
                  component="span"
                  sx={visuallyHidden}
                >
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
EnhancedTableHead.propTypes = {
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  onRequestSort: PropTypes.func.isRequired,
};

// --- Delta Cell ---
function DeltaCell({ value, delta, isDark }) {
  const positive = delta > 0;
  const negative = delta < 0;
  const color = positive ? "#16a34a" : negative ? "#dc2626" : "inherit";
  return (
    <TableCell
      align="left"
      className={delta !== 0 ? "cell-flash" : ""}
      sx={{
        padding: "2px 4px",
        fontSize: "0.75rem",
        color: delta !== 0 ? color : "inherit",
        fontWeight: delta !== 0 ? 600 : 400,
        whiteSpace: "nowrap",
      }}
    >
      {value?.toLocaleString()}
      {delta !== 0 && (
        <Box
          component="span"
          sx={{ ml: 0.5, fontSize: "0.65rem", opacity: 0.9 }}
        >
          {positive ? "\u25B2" : "\u25BC"}
          {Math.abs(delta).toLocaleString()}
        </Box>
      )}
    </TableCell>
  );
}

// --- Percentage Badge ---
function PctBadge({ value }) {
  const rounded = Math.round(value);
  const bg =
    rounded >= 95
      ? "rgba(22,163,74,0.2)"
      : rounded >= 85
        ? "rgba(250,204,21,0.2)"
        : "rgba(156,163,175,0.2)";
  const fg = rounded >= 95 ? "#16a34a" : rounded >= 85 ? "#ca8a04" : "#9ca3af";
  return (
    <TableCell
      align="left"
      sx={{ padding: "2px 4px" }}
    >
      <Box
        sx={{
          display: "inline-block",
          px: 0.5,
          py: 0.25,
          borderRadius: "4px",
          fontSize: "0.65rem",
          fontWeight: 600,
          backgroundColor: bg,
          color: fg,
        }}
      >
        {rounded}%
      </Box>
    </TableCell>
  );
}

// ============================================================
// Main App
// ============================================================
function App() {
  const [isDark, setIsDark] = useState(false);
  const [raceType, setRaceType] = useState("");
  const [availableRaceTypes, setAvailableRaceTypes] = useState([]);
  const [loadingRaceTypes, setLoadingRaceTypes] = useState(true);
  const [raceDate, setRaceDate] = useState("");

  const [rows, setRows] = useState([]);
  const [sortState, setSortState] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [openGuide, setOpenGuide] = useState(false);
  const [pollInterval, setPollInterval] = useState(3000);

  const [time, setTime] = useState(new Date().toLocaleString());
  const [status, setStatus] = useState(false);

  // --- Fetch available race types ---
  useEffect(() => {
    setLoadingRaceTypes(true);
    axios
      .get(`${API_BASE}/api/race-types`)
      .then((res) => {
        const types = res.data.raceTypes || [];
        setAvailableRaceTypes(types);
        setRaceDate(res.data.date || "");
        if (types.length > 0) {
          setRaceType((prev) => prev || types[0].raceType);
        }
        setLoadingRaceTypes(false);
      })
      .catch(() => setLoadingRaceTypes(false));
  }, []);

  // --- Clock ---
  useEffect(() => {
    const t = setInterval(() => setTime(new Date().toLocaleString()), 1000);
    return () => clearInterval(t);
  }, []);

  // --- Status: green when we have rows to display ---
  useEffect(() => {
    setStatus(rows.length > 0);
  }, [rows]);

  // --- Fetch race data with deltas ---
  const fetchRaceData = useCallback(() => {
    if (!raceType) return;

    axios
      .get(`${API_BASE}/api/race-data-with-delta?race_type=${raceType}`)
      .then((res) => {
        const data = res.data.map((item, i) => ({
          id: i,
          RaceNumber: String(item.RaceNumber),
          Selection: item.Selection,
          win: item.win,
          place: item.place,
          winShare: item.winShare,
          WinLimit: item.winLimit,
          Percentage: item.Percentage,
          winDelta: item.winDelta || 0,
          placeDelta: item.placeDelta || 0,
          winShareDelta: item.winShareDelta || 0,
          winLimitDelta: item.winLimitDelta || 0,
          isNew: item.isNew || false,
        }));
        setRows(data);

        // Flash animation is handled by DeltaCell via winDelta !== 0
      })
      .catch((err) => {
        console.error("Error fetching data:", err);
      });
  }, [raceType]);

  useEffect(() => {
    fetchRaceData();
    const id = setInterval(fetchRaceData, pollInterval);
    return () => clearInterval(id);
  }, [fetchRaceData, pollInterval]);

  // --- Sorting ---
  const handleRequestSort = (property, raceNumber) => {
    const cur = sortState[raceNumber] || { order: "asc", orderBy: "Selection" };
    const isAsc = cur.orderBy === property && cur.order === "asc";
    setSortState({
      ...sortState,
      [raceNumber]: { order: isAsc ? "desc" : "asc", orderBy: property },
    });
  };

  // --- Grouped rows ---
  const groupedRows = useMemo(() => {
    const groups = {};
    for (let i = 1; i <= 12; i++) groups[i] = [];
    rows.forEach((row) => {
      const n = parseInt(row.RaceNumber);
      if (n >= 1 && n <= 12) groups[n].push(row);
    });
    return groups;
  }, [rows]);

  // --- Top movers ---
  const topMovers = useMemo(() => {
    return [...rows]
      .filter((r) => r.winDelta !== 0)
      .sort((a, b) => Math.abs(b.winDelta) - Math.abs(a.winDelta))
      .slice(0, 5);
  }, [rows]);

  // --- Delete ---
  const handleDeleteAll = () => {
    axios
      .delete(`${API_BASE}/api/clear-data`)
      .then(() => {
        setRows([]);

        setOpenDialog(false);
      })
      .catch(() => {
        setOpenDialog(false);
      });
  };

  const raceTypeOptions = availableRaceTypes.map((rt) => rt.raceType);

  return (
    <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
      <CssBaseline />
      <div>
        {/* ===== HEADER CONTROLS ===== */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            width: "100%",
            maxWidth: "800px",
            mx: "auto",
          }}
        >
          {/* Row 1: Race selector + polling + theme + clear */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              flexWrap: "wrap",
            }}
          >
            <Autocomplete
              freeSolo
              options={raceTypeOptions}
              value={raceType}
              onChange={(e, v) => v && setRaceType(v)}
              onInputChange={(e, v, reason) =>
                reason === "input" && setRaceType(v.toUpperCase())
              }
              loading={loadingRaceTypes}
              getOptionLabel={(o) => o}
              renderOption={(props, option) => {
                const info = availableRaceTypes.find(
                  (rt) => rt.raceType === option,
                );
                return (
                  <li
                    {...props}
                    key={option}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>{option}</Typography>
                      {info && (
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", ml: 2 }}
                        >
                          {info.count.toLocaleString()} records
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              sx={{ flex: 1, minWidth: 160 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Race Type"
                  size="small"
                  placeholder={
                    loadingRaceTypes ? "Loading..." : "Select or type"
                  }
                />
              )}
            />

            <ToggleButtonGroup
              value={pollInterval}
              exclusive
              onChange={(e, v) => v !== null && setPollInterval(v)}
              size="small"
            >
              <ToggleButton
                value={1000}
                sx={{ px: 1.5, fontSize: "0.7rem" }}
              >
                1s
              </ToggleButton>
              <ToggleButton
                value={3000}
                sx={{ px: 1.5, fontSize: "0.7rem" }}
              >
                3s
              </ToggleButton>
              <ToggleButton
                value={5000}
                sx={{ px: 1.5, fontSize: "0.7rem" }}
              >
                5s
              </ToggleButton>
              <ToggleButton
                value={10000}
                sx={{ px: 1.5, fontSize: "0.7rem" }}
              >
                10s
              </ToggleButton>
            </ToggleButtonGroup>

            <IconButton
              onClick={() => setIsDark((p) => !p)}
              size="small"
              sx={{ fontSize: "1.2rem" }}
            >
              {isDark ? "\u2600\uFE0F" : "\uD83C\uDF19"}
            </IconButton>

            <IconButton
              onClick={() => setOpenGuide(true)}
              size="small"
              sx={{
                fontSize: "1rem",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "6px",
                width: 32,
                height: 32,
              }}
            >
              ?
            </IconButton>

            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setOpenDialog(true)}
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                whiteSpace: "nowrap",
              }}
            >
              Clear
            </Button>
          </Box>

          {/* Status bar */}
          {raceType && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.75,
                px: 2,
                borderRadius: "6px",
                backgroundColor: status
                  ? isDark
                    ? "rgba(22,163,74,0.12)"
                    : "#f0fdf4"
                  : isDark
                    ? "rgba(220,38,38,0.12)"
                    : "#fef2f2",
                border: `1px solid ${status ? (isDark ? "#166534" : "#bbf7d0") : isDark ? "#7f1d1d" : "#fecaca"}`,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: status ? "#22c55e" : "#ef4444",
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600 }}
              >
                {raceType}
              </Typography>
              {raceDate && (
                <Chip
                  label={raceDate}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem" }}
                />
              )}
              <Chip
                label={`${rows.length} horses`}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.7rem" }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  ml: "auto",
                  fontSize: "0.75rem",
                }}
              >
                {time}
              </Typography>
            </Box>
          )}

          {/* Quick-pick chips */}
          {availableRaceTypes.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
                justifyContent: "center",
              }}
            >
              {availableRaceTypes.map((rt) => (
                <Chip
                  key={rt.raceType}
                  label={rt.raceType}
                  size="small"
                  variant={raceType === rt.raceType ? "filled" : "outlined"}
                  color={raceType === rt.raceType ? "primary" : "default"}
                  onClick={() => setRaceType(rt.raceType)}
                  sx={{
                    cursor: "pointer",
                    fontWeight: raceType === rt.raceType ? 700 : 400,
                  }}
                />
              ))}
            </Box>
          )}

          {loadingRaceTypes && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary" }}
            >
              Checking available race types for today...
            </Typography>
          )}
          {!loadingRaceTypes && availableRaceTypes.length === 0 && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary" }}
            >
              No race types found for today. Type a race code manually above.
            </Typography>
          )}
        </Box>

        {/* ===== TOP MOVERS ===== */}
        {topMovers.length > 0 && (
          <Box
            sx={{ px: 2, py: 1, maxWidth: "800px", mx: "auto" }}
            className="slide-in"
          >
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 700, color: "text.secondary" }}
            >
              Biggest Movers
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                overflowX: "auto",
                pb: 1,
              }}
            >
              {topMovers.map((m) => (
                <Paper
                  key={`${m.RaceNumber}-${m.Selection}`}
                  sx={{
                    minWidth: 150,
                    p: 1.5,
                    borderRadius: "8px",
                    borderLeft: `4px solid ${m.winDelta > 0 ? "#16a34a" : "#dc2626"}`,
                    flexShrink: 0,
                  }}
                  elevation={2}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: "text.secondary" }}
                    >
                      R{m.RaceNumber}
                    </Typography>
                    <Chip
                      label={`#${m.Selection}`}
                      size="small"
                      sx={{ fontWeight: 700, fontSize: "0.7rem", height: 20 }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: m.winDelta > 0 ? "#16a34a" : "#dc2626",
                      mt: 0.5,
                      lineHeight: 1.2,
                    }}
                  >
                    {m.win.toLocaleString()}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: m.winDelta > 0 ? "#16a34a" : "#dc2626",
                        fontWeight: 600,
                      }}
                    >
                      {m.winDelta > 0 ? "+" : ""}
                      {m.winDelta.toLocaleString()}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: m.winDelta > 0 ? "#16a34a" : "#dc2626",
                        fontWeight: 600,
                      }}
                    >
                      {m.winDelta > 0 ? "+" : ""}
                      {m.winDelta} bets
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {/* ===== DELETE DIALOG ===== */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Delete all records from the database? This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              onClick={handleDeleteAll}
              color="error"
              autoFocus
            >
              Yes, Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* ===== INFO GUIDE ===== */}
        <Dialog
          open={openGuide}
          onClose={() => setOpenGuide(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: "12px", maxHeight: "85vh" } }}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
            How to Use HorseAI
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {/* OVERVIEW */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}
            >
              Overview
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 2 }}
            >
              HorseAI monitors live horse racing betting data and helps you
              identify profitable arbitrage opportunities. Data is fetched from
              CityBet in real-time and processed to highlight significant
              movements in the final seconds before a race starts.
            </Typography>

            {/* RACE TYPE */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5 }}
            >
              Race Type Selector
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 1 }}
            >
              Each code represents a specific race meeting (venue + date).
              Available race types are auto-detected for today. Click a chip to
              switch, or type any code manually.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Chip
                label="49A"
                size="small"
                color="primary"
              />
              <Chip
                label="50A"
                size="small"
                variant="outlined"
              />
              <Typography
                variant="caption"
                sx={{ alignSelf: "center", color: "text.secondary" }}
              >
                Highlighted = currently selected
              </Typography>
            </Box>

            {/* POLLING */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5 }}
            >
              Refresh Speed (1s / 3s / 5s / 10s)
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 2 }}
            >
              Controls how often data is fetched from the server. Use <b>1s</b>{" "}
              or <b>3s</b> during the critical window right before a race for
              near-real-time updates. Use
              <b> 5s</b> or <b>10s</b> when monitoring casually to reduce server
              load.
            </Typography>

            {/* STATUS BAR */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5 }}
            >
              Status Bar
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: "#22c55e",
                }}
              />
              <Typography variant="body2">
                <b>Green</b> = Data is flowing from the API
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: "#ef4444",
                }}
              />
              <Typography variant="body2">
                <b>Red</b> = No data available (race may not have started or API
                is down)
              </Typography>
            </Box>

            {/* TABLE COLUMNS */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5 }}
            >
              Race Tables
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 1 }}
            >
              Each card represents one race (Race 1-12). The badge in the header
              shows how many horses have qualifying data in that race.
            </Typography>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ mb: 2 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Column</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Meaning</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <b>Horse</b>
                    </TableCell>
                    <TableCell>
                      Horse number / selection ID in the race
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <b>Win</b>
                    </TableCell>
                    <TableCell>
                      Aggregated win betting volume (winAmount x5) from win-only
                      entries. Higher = more money backing this horse to win.
                      Watch for sudden spikes in the final seconds before a
                      race.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <b>Place</b>
                    </TableCell>
                    <TableCell>
                      Place bets from place-only entries (0/x format). Compare
                      with Win — if Win is much higher than Place, bettors are
                      confident in an outright win.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <b>Share%</b>
                    </TableCell>
                    <TableCell>
                      What percentage of the race's total win bets are on this
                      horse. This is the most important column — it instantly
                      shows the market favorite. A horse at{" "}
                      <b style={{ color: "#16a34a" }}>18%+</b> (green) is
                      heavily backed. At{" "}
                      <b style={{ color: "#ed6c02" }}>14%+</b> (orange) it's a
                      notable contender. In a 10-horse race, an even split would
                      be 10% each — anything well above that signals strong
                      market confidence.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <b>Limit</b>
                    </TableCell>
                    <TableCell>
                      Win limit from the bookmaker (WinPlaceLimit left side /
                      5). Lower limit = bookmaker is restricting bets on this
                      horse. A horse with a low limit and high Win Share is a
                      strong signal.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <b>%</b>
                    </TableCell>
                    <TableCell>
                      Pool fill rate = (ticketPrice / $5) x 100. This is a
                      market-level indicator, not per-horse. When it reaches
                      100%, the betting pool is full. During buildup it shows
                      how close the market is to capacity.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* MOVEMENT INDICATORS */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5 }}
            >
              Movement Indicators
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 1 }}
            >
              After the first data fetch, subsequent fetches show how values
              changed compared to the previous fetch:
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mb: 2,
                pl: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    color: "#16a34a",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                  }}
                >
                  150 {"\u25B2"}25
                </Typography>
                <Typography variant="body2">
                  Green + up arrow = value increased (more money coming in)
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    color: "#dc2626",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                  }}
                >
                  80 {"\u25BC"}15
                </Typography>
                <Typography variant="body2">
                  Red + down arrow = value decreased
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 4,
                    height: 20,
                    backgroundColor: "#16a34a",
                    borderRadius: 1,
                    mr: 0.5,
                  }}
                />
                <Typography variant="body2">
                  <b>Green left border</b> = big mover (win increased by &gt;5
                  bets since last fetch) — pay attention!
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 4,
                    height: 20,
                    backgroundColor: "#dc2626",
                    borderRadius: 1,
                    mr: 0.5,
                  }}
                />
                <Typography variant="body2">
                  <b>Red left border</b> = big drop (&gt;5 bets) — money moving
                  away from this horse
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    px: 0.5,
                    py: 0.25,
                    backgroundColor: "rgba(59,130,246,0.1)",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#3b82f6", fontWeight: 600 }}
                  >
                    NEW
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Blue background row = horse just appeared in the data (wasn't
                  in previous fetch)
                </Typography>
              </Box>
            </Box>

            {/* PERCENTAGE BADGES */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5 }}
            >
              Percentage Badges
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mb: 2,
                pl: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    borderRadius: "4px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    backgroundColor: "rgba(22,163,74,0.2)",
                    color: "#16a34a",
                  }}
                >
                  96%
                </Box>
                <Typography variant="body2">
                  <b>Green (95%+)</b> — very high pool fill, strong betting
                  activity
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    borderRadius: "4px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    backgroundColor: "rgba(250,204,21,0.2)",
                    color: "#ca8a04",
                  }}
                >
                  89%
                </Box>
                <Typography variant="body2">
                  <b>Yellow (85-94%)</b> — moderate pool fill
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    borderRadius: "4px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    backgroundColor: "rgba(156,163,175,0.2)",
                    color: "#9ca3af",
                  }}
                >
                  80%
                </Box>
                <Typography variant="body2">
                  <b>Gray (&lt;85%)</b> — lower pool fill, less activity
                </Typography>
              </Box>
            </Box>

            {/* BIGGEST MOVERS */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5 }}
            >
              Biggest Movers Bar
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 1 }}
            >
              Appears after the second data fetch. Shows the top 5 horses across
              all races with the largest change in win bets. Each card shows:
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                mb: 2,
                pl: 1,
              }}
            >
              <Typography variant="body2">
                &#8226; <b>R3</b> — Race number
              </Typography>
              <Typography variant="body2">
                &#8226; <b>#5</b> — Horse number
              </Typography>
              <Typography variant="body2">
                &#8226; <b>Large number</b> — Current win value
              </Typography>
              <Typography variant="body2">
                &#8226; <b>+25 bets</b> — Change in win bets from last fetch
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 2 }}
            >
              Green border = win value increasing. Red border = decreasing.
              These are the horses experiencing the most action right now — the
              core signal for arbitrage opportunities.
            </Typography>

            {/* STRATEGY TIP */}
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: "8px", mb: 1 }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, mb: 0.5 }}
              >
                Quick Strategy Guide
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary" }}
              >
                1. Set refresh to <b>1s</b> or <b>3s</b> as race time
                approaches.
                <br />
                2. Watch the <b>Biggest Movers</b> bar — sudden spikes indicate
                smart money moving in.
                <br />
                3. Check <b>Win Share%</b> — the market favorite will have a
                significantly higher share than an even split (e.g. 18% vs 10%
                in a 10-horse race).
                <br />
                4. Look for horses with <b>high Win Share + low Limit</b> — the
                bookmaker is capping bets on this horse, signaling they expect
                it to win.
                <br />
                5. Compare <b>Win vs Place</b> — if Win is much higher than
                Place, the market is confident in an outright win, not just a
                top-3 finish.
                <br />
                6. Green-bordered rows with <b>
                  rapidly increasing win bets
                </b>{" "}
                in the last few seconds are your primary arbitrage signals.
              </Typography>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenGuide(false)}
              variant="contained"
              size="small"
            >
              Got it
            </Button>
          </DialogActions>
        </Dialog>

        {/* ===== RACE GRID ===== */}
        <Box
          sx={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
            gap: 1,
            p: 1,
          }}
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((raceNumber) => {
            const { order, orderBy } = sortState[raceNumber] || {
              order: "asc",
              orderBy: "Selection",
            };
            const raceRows = groupedRows[raceNumber] || [];
            const sorted = stableSort(raceRows, getComparator(order, orderBy));

            return (
              <Paper
                key={raceNumber}
                sx={{
                  m: 0.5,
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                }}
                elevation={0}
              >
                {/* Card header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 1.5,
                    py: 0.75,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor: isDark
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(0,0,0,0.02)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700 }}
                  >
                    Race {raceNumber}
                  </Typography>
                  <Chip
                    label={raceRows.length}
                    size="small"
                    color={raceRows.length > 0 ? "primary" : "default"}
                    sx={{
                      minWidth: 28,
                      height: 20,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                    }}
                  />
                </Box>

                {/* Table */}
                <TableContainer sx={{ maxHeight: 320, overflow: "auto" }}>
                  <Table
                    size="small"
                    stickyHeader
                  >
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={(prop) =>
                        handleRequestSort(prop, raceNumber)
                      }
                    />
                    <TableBody>
                      {sorted.length > 0 ? (
                        sorted.map((row) => {
                          const bigMove = Math.abs(row.winDelta) > 5;
                          return (
                            <TableRow
                              hover
                              key={row.id}
                              sx={{
                                borderLeft: bigMove
                                  ? `3px solid ${row.winDelta > 0 ? "#16a34a" : "#dc2626"}`
                                  : "3px solid transparent",
                                backgroundColor: row.isNew
                                  ? isDark
                                    ? "rgba(59,130,246,0.08)"
                                    : "rgba(59,130,246,0.05)"
                                  : bigMove
                                    ? row.winDelta > 0
                                      ? isDark
                                        ? "rgba(22,163,74,0.06)"
                                        : "rgba(22,163,74,0.04)"
                                      : isDark
                                        ? "rgba(220,38,38,0.06)"
                                        : "rgba(220,38,38,0.04)"
                                    : "inherit",
                              }}
                            >
                              <TableCell
                                align="left"
                                sx={{
                                  padding: "2px 6px",
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                }}
                              >
                                {row.Selection}
                              </TableCell>
                              <DeltaCell
                                value={row.win}
                                delta={row.winDelta}
                                isDark={isDark}
                              />
                              <DeltaCell
                                value={row.place}
                                delta={row.placeDelta}
                                isDark={isDark}
                              />
                              <TableCell
                                align="left"
                                sx={{
                                  padding: "2px 6px",
                                  fontSize: "0.8rem",
                                  fontWeight: row.winShare >= 15 ? 700 : 400,
                                  color:
                                    row.winShare >= 18
                                      ? "success.main"
                                      : row.winShare >= 14
                                        ? "warning.main"
                                        : "text.primary",
                                }}
                              >
                                {row.winShare != null
                                  ? `${row.winShare}%`
                                  : "-"}
                              </TableCell>
                              <DeltaCell
                                value={row.WinLimit}
                                delta={row.winLimitDelta}
                                isDark={isDark}
                              />
                              <PctBadge value={row.Percentage} />
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={{
                              padding: "16px 4px",
                              fontSize: "0.8rem",
                              color: "text.secondary",
                            }}
                          >
                            No data
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })}
        </Box>
      </div>
    </ThemeProvider>
  );
}

export default App;
